import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    Typography,
    Box,
    Paper,
    useTheme,
    IconButton,
    Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { RichTextEditor } from "../../../../app/RichTextEditor";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { Localized, useLocalization } from "@fluent/react";

interface LimitsEditDialogProps {
    open: boolean;
    loading?: boolean;
    onClose: () => void;
    onSave: (values: {
        lowMax: number;
        mediumMax: number;
        descriptions: {
            low: string;
            medium: string;
            high: string;
        };
    }) => void;
    initialValues: {
        lowMax: number;
        mediumMax: number;
        descriptions: {
            low: string;
            medium: string;
            high: string;
        };
    };
}

type DescriptionLevel = "low" | "medium" | "high";

const LimitsEditDialog: React.FC<LimitsEditDialogProps> = ({
    open,
    loading = false,
    onClose,
    onSave,
    initialValues,
}) => {
    const theme = useTheme();
    const { l10n } = useLocalization();

    const [lowMax, setLowMax] = useState(initialValues.lowMax);
    const [mediumMax, setMediumMax] = useState(initialValues.mediumMax);
    const [descriptions, setDescriptions] = useState(initialValues.descriptions);
    const [rangeError, setRangeError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setLowMax(initialValues.lowMax);
            setMediumMax(initialValues.mediumMax);
            setDescriptions(initialValues.descriptions);
            setRangeError(null);
        }
    }, [open, initialValues]);

    const handleLowMaxChange = (value: string) => {
        const num = Number(value);

        if (!isNaN(num) && num <= 99) {
            setLowMax(num);
            setRangeError(null);
        }
    };

    const handleMediumMaxChange = (value: string) => {
        const num = Number(value);

        if (!isNaN(num) && num <= 99) {
            setMediumMax(num);
            setRangeError(null);
        }
    };

    const validateRanges = () => {
        if (!Number.isInteger(lowMax) || !Number.isInteger(mediumMax)) {
            return "risk-analysis-val-whole-numbers";
        }

        if (lowMax < 1 || lowMax > 99 || mediumMax < 1 || mediumMax > 99) {
            return "risk-analysis-val-range-1-99";
        }

        if (lowMax >= mediumMax) {
            return "risk-analysis-val-low-before-medium";
        }

        return null;
    };

    const handleSave = () => {
        const validationError = validateRanges();

        if (validationError) {
            setRangeError(validationError);
            return;
        }

        onSave({
            lowMax,
            mediumMax,
            descriptions,
        });
    };

    const [expandedLevel, setExpandedLevel] = useState<DescriptionLevel | null>(null);

    const handleToggleDescriptionEditor = (level: DescriptionLevel) => {
        setExpandedLevel(prev => (prev === level ? null : level));
    };

    const handleDescriptionChange = (level: DescriptionLevel, value: string) => {
        setDescriptions(prev => ({
            ...prev,
            [level]: value,
        }));
    };

    const lowProportion = lowMax;
    const mediumProportion = mediumMax - lowMax;
    const highProportion = 100 - mediumMax;
    const hasRangeFieldError = Boolean(rangeError);

    const rows = [
        {
            level: "low" as const,
            label: <Localized id="risk-analysis-low">_LOW</Localized>,
            color: theme.palette.success.main,
            from: 1,
            to: (
                <TextField
                    type="number"
                    value={lowMax}
                    onChange={e => handleLowMaxChange(e.target.value)}
                    error={hasRangeFieldError}
                    size="small"
                    sx={{ width: 72 }}
                    inputProps={{ min: 1, max: 99 }}
                    InputProps={{ sx: { height: 32 } }}
                />
            ),
            rangeLabel: `1-${lowMax}`,
            description: descriptions.low,
        },
        {
            level: "medium" as const,
            label: <Localized id="risk-analysis-medium">_MEDIUM</Localized>,
            color: theme.palette.warning.main,
            from: lowMax + 1,
            to: (
                <TextField
                    type="number"
                    value={mediumMax}
                    onChange={e => handleMediumMaxChange(e.target.value)}
                    error={hasRangeFieldError}
                    size="small"
                    sx={{ width: 72 }}
                    inputProps={{ min: lowMax + 1, max: 99 }}
                    InputProps={{ sx: { height: 32 } }}
                />
            ),
            rangeLabel: `${lowMax + 1}-${mediumMax}`,
            description: descriptions.medium,
        },
        {
            level: "high" as const,
            label: <Localized id="risk-analysis-critical">_HIGH</Localized>,
            color: theme.palette.error.main,
            from: mediumMax + 1,
            operator: "≥",
            to: null,
            description: descriptions.high,
        }
    ];

    return (
        <>
            <Dialog open={open} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Localized id="risk-analysis-edit-ranges">_Edit Ranges</Localized>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    height: 32,
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    boxShadow: 1,
                                    bgcolor: "grey.200",
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: lowProportion,
                                        minWidth: 50,
                                        bgcolor: theme.palette.success.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "rgba(255,255,255,0.9)",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        px: 1,
                                    }}
                                >
                                    1-{lowMax}
                                </Box>

                                <Box
                                    sx={{
                                        flex: mediumProportion,
                                        minWidth: 50,
                                        bgcolor: theme.palette.warning.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "rgba(255,255,255,0.9)",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        px: 1,
                                    }}
                                >
                                    {lowMax + 1}-{mediumMax}
                                </Box>

                                <Box
                                    sx={{
                                        flex: highProportion,
                                        minWidth: 80,
                                        bgcolor: theme.palette.error.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "rgba(255,255,255,0.9)",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        px: 1,
                                    }}
                                >
                                    {`≥ ${mediumMax + 1}`}
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        bgcolor: "grey.50",
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Box sx={{ width: 72 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            fontWeight={700}
                                            sx={{
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            <Localized id="level">_Level</Localized>
                                        </Typography>
                                    </Box>

                                    <Box sx={{ width: 168 }} />

                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            pl: 1,
                                            borderLeft: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            fontWeight={700}
                                            sx={{
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            <Localized id="interpretation">Interpretation</Localized>
                                        </Typography>
                                    </Box>

                                    <Box sx={{ width: 40 }} />
                                </Stack>
                                <Stack spacing={0}>
                                    {rows.map((row, index) => (
                                        <Box
                                            key={row.level}
                                            sx={{
                                                borderBottom:
                                                    index < rows.length - 1
                                                        ? `1px solid ${theme.palette.divider}`
                                                        : "none",
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{
                                                    px: 1.5,
                                                    py: 1,
                                                }}
                                            >
                                                <Box sx={{ width: 72 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: row.color,
                                                            fontWeight: 700,
                                                            letterSpacing: 0.3,
                                                            textTransform: "uppercase",
                                                        }}
                                                    >
                                                        {row.label}
                                                    </Typography>
                                                </Box>

                                                {row.to ? (
                                                    <>
                                                        <Box
                                                            sx={{
                                                                width: 52,
                                                                textAlign: "right",
                                                                fontVariantNumeric: "tabular-nums",
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.from}
                                                            </Typography>
                                                        </Box>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                            sx={{ width: 20, textAlign: "center" }}
                                                        >
                                                            —
                                                        </Typography>

                                                        <Box
                                                            sx={{
                                                                width: 80,
                                                                display: "flex",
                                                                justifyContent: "flex-start",
                                                            }}
                                                        >
                                                            {row.to}
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: 168,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            ≥ {row.from}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        pl: 1,
                                                        borderLeft: `1px solid ${theme.palette.divider}`,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            typography: "caption",
                                                            color: row.description ? "text.secondary" : "text.disabled",
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",

                                                            "& p": {
                                                                m: 0,
                                                            },
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                row.description ||
                                                                `<i>${l10n.getString("empty-description", null, "Empty")}</i>`,
                                                        }}
                                                    />
                                                </Box>

                                                <IconButton
                                                    size="small"
                                                    color={expandedLevel === row.level ? "primary" : "default"}
                                                    onClick={() => handleToggleDescriptionEditor(row.level)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>

                                            {expandedLevel === row.level && (
                                                <Box
                                                    sx={{
                                                        px: 1.5,
                                                        py: 1,
                                                        borderTop: `1px solid ${theme.palette.divider}`,
                                                    }}
                                                >
                                                    <RichTextEditor
                                                        value={descriptions[row.level]}
                                                        onValueChange={value =>
                                                            handleDescriptionChange(row.level, value)
                                                        }
                                                        label={null}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Box>

                        {rangeError && (
                            <Alert severity="error">
                                {l10n.getString(rangeError, null, rangeError)}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <SaveButton onClick={handleSave} loading={loading} />
                    <CancelButton onClick={onClose} loading={loading} />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LimitsEditDialog;